import {
  BN,
  formatDecimal,
  isNormalPositive,
} from '@bifrost-platform/bifront-sdk-react-biholder';
import { motion } from 'framer-motion';
import hexToRgba from 'hex-to-rgba';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import ImageLogoBifi from '@/assets/images/image-logo-bifi.svg';
import ImageLogoEverdex from '@/assets/images/image-logo-everdex.svg';
import { GlassPanel } from '@/components/atom/GlassPanel';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@/components/atom/Tooltip';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import useEnv from '@/hooks/useEnv';
import {
  formatAmount,
  formatAmountOnHover,
  formatNumber,
} from '@/libs/formatNumber';
import {
  eventBifiWithdrawClick,
  eventEverdexRemoveLiquidityClick,
  eventEverdexUnstakingClick,
} from '@/libs/gtag';
import {
  sendSentryEventBifiWithdrawClick,
  sendSentryEventEverdexRemoveLiquidityClick,
  sendSentryEventEverdexUnstakingClick,
} from '@/libs/sentry';
import { dialogActionAtom } from '@/store/uiStore';
import { dashboardCardVariant } from '@/styles/motion';
import TokenIcon from '../TokenIcon';
import TokenPairIcon from '../TokenPairIcon';
import Button from '../atom/Button';
import Division from '../atom/Division';
import { FlexBox } from '../atom/FlexBox';
import InputPanel from '../atom/InputPanel';
import Skeleton from '../atom/Skeleton';
import { StyledText } from '../atom/Text';

export interface Props extends React.PropsWithChildren {
  header?: React.ReactNode;
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  grow?: number;
}

const Container = styled(GlassPanel)``;
const BadgeContainer = styled(FlexBox)`
  display: flex;
  background-color: ${(p) => p.theme.colors.black};
  padding: 0 ${(p) => p.theme.size.xxs};
  border-radius: ${(p) => p.theme.size.md};
  justify-content: center;
  align-items: center;
  gap: ${(p) => p.theme.size.xxs};
  height: ${(p) => p.theme.size.xl};
  white-space: nowrap;
`;
const Header = styled(FlexBox)`
  padding: 4px 16px;
`;
const HeaderTag = styled.div`
  min-height: 44px;
  padding: 8px;
  font-size: ${({ theme }) => theme.size.lg};
  ${({ theme }) => theme.font.black};
  color: ${({ theme }) => theme.colors.green};
  border-radius: 12px;
  background-color: ${({ theme }) => hexToRgba(theme.colors.white, 0.08)};
  backdrop-filter: blur(4px);
`;

export function EarnDetailRow({
  children,
  isLoading,
}: React.PropsWithChildren & { isLoading?: boolean }) {
  return (
    <>
      {isLoading ? (
        <Skeleton $height={56} />
      ) : (
        <InputPanel $padding="md">
          <FlexBox
            $flex
            $justify="space-between"
            $align="center"
            $gap="xxs"
            $height={24}
          >
            {children}
          </FlexBox>
        </InputPanel>
      )}
    </>
  );
}

export default function EarnDetailPanel({
  header,
  width,
  height,
  minWidth,
  minHeight,
  grow,
  children,
}: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <Container
      as={motion.div}
      variants={dashboardCardVariant}
      $flex
      $grow={grow}
      $direction="column"
      $gap="lg"
      $padding="xl"
      $rounded="xxl"
      $width={width}
      $height={height}
      $minWidth={minWidth}
      $minHeight={minHeight}
    >
      <FlexBox $flex $direction="column" $gap="xxs">
        <FlexBox $flex $direction="column" $gap="xxs">
          {header}
          <Division />
        </FlexBox>
        <FlexBox $flex $direction="column" $gap="xxs">
          <Header $flex $justify="space-between" $align="center">
            <StyledText>{t('depositValue')}</StyledText>
            <StyledText>{t('reward')}</StyledText>
          </Header>
          <FlexBox $flex $direction="column" $gap="xs">
            {children}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </Container>
  );
}

export function EarnDetailBifi({
  amount,
  interestAmount,
  rewardAmount,
  isLoading,
}: {
  amount?: string;
  interestAmount?: string;
  rewardAmount?: string;
  isLoading?: boolean;
}) {
  // translate
  const { t } = useTranslation('dashboard');

  // env
  const { isTestnet, tokenBifrostBtcusd } = useEnv();

  // currency
  const { getCurrencyString } = useGetCurrencyString();

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);

  // memo
  const depositAmount = useMemo(
    () => new BN(amount ?? 0).plus(interestAmount ?? 0),
    [amount, interestAmount]
  );
  const totalAmount = useMemo(
    () => depositAmount.plus(rewardAmount ?? 0),
    [depositAmount, rewardAmount]
  );

  // handler
  const handleWithdrawClick = useCallback(() => {
    eventBifiWithdrawClick(isTestnet);
    sendSentryEventBifiWithdrawClick(isTestnet);
    setDialogAction({ isShow: true, type: 'bifi:withdraw' });
  }, [setDialogAction, isTestnet]);

  return (
    <EarnDetailPanel
      header={
        <FlexBox $flex $justify="space-between" $align="center">
          <ImageLogoBifi />

          <Tooltip placement="top" offset={16}>
            <TooltipTrigger>
              <HeaderTag>
                + {getCurrencyString(rewardAmount, tokenBifrostBtcusd)}
              </HeaderTag>
            </TooltipTrigger>
            <TooltipContent>
              <TooltipArrow />
              <StyledText $fontSize="xs" $fontWeight="medium">
                {formatAmountOnHover(rewardAmount)}{' '}
                {tokenBifrostBtcusd?.symbol ?? 'BtcUSD'}
              </StyledText>
            </TooltipContent>
          </Tooltip>
        </FlexBox>
      }
      minHeight={216}
      grow={1}
    >
      <EarnDetailRow isLoading={isLoading}>
        <FlexBox $flex $align="center" $gap="xxs">
          <TokenIcon isHideChain size={24} {...tokenBifrostBtcusd} />
          <Tooltip placement="top" offset={16}>
            <TooltipTrigger asChild>
              {getCurrencyString(totalAmount, tokenBifrostBtcusd)}
            </TooltipTrigger>
            <TooltipContent>
              <TooltipArrow />
              <StyledText $fontSize="xs" $fontWeight="medium">
                <div>
                  {t('bifiDepositPlusInterest')}: {formatAmount(depositAmount)}{' '}
                  {tokenBifrostBtcusd?.symbol ?? 'BtcUSD'}
                </div>
                <div>
                  {t('bifiDepositReward')}: {formatAmount(rewardAmount)}{' '}
                  {tokenBifrostBtcusd?.symbol ?? 'BtcUSD'}
                </div>
              </StyledText>
            </TooltipContent>
          </Tooltip>
        </FlexBox>
        <FlexBox $flex $align="center" $gap="xxs">
          <FlexBox $flex $align="center" $gap="xxs">
            <Button
              $variant="primary"
              $padding="xxs"
              $rounded="xs"
              disabled={!isNormalPositive(depositAmount, true)}
              onClick={handleWithdrawClick}
            >
              <StyledText $fontSize="xs" $fontWeight="medium">
                {t('bifiWithdraw')}
              </StyledText>
            </Button>
          </FlexBox>
        </FlexBox>
      </EarnDetailRow>
    </EarnDetailPanel>
  );
}

export function EarnDetailEverdex({
  amount,
  apr,
  stakeAmount,
  rewardAmount,
  isLoading,
}: {
  amount?: string;
  apr?: string;
  stakeAmount?: string;
  rewardAmount?: string;
  isLoading?: boolean;
}) {
  // translate
  const { t } = useTranslation('dashboard');

  // env
  const {
    isTestnet,
    everdexLpRewardPool,
    tokenBifrostBfc,
    tokenBifrostEverdexStakeLp,
  } = useEnv();

  // currency
  const { formatCurrencyValue, getNumberWithCurrencyReplace } =
    useGetCurrencyString();

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);

  // memo
  const lpAmount = useMemo(
    () =>
      formatNumber(
        new BN(isNormalPositive(amount) ? amount ?? 0 : 0).plus(
          isNormalPositive(stakeAmount) ? stakeAmount ?? 0 : 0
        )
      ),
    [amount, stakeAmount]
  );
  const amountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(amount) ? amount : '0',
        tokenBifrostEverdexStakeLp
      ),
    [amount, formatCurrencyValue, tokenBifrostEverdexStakeLp]
  );
  const rewardAmountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(rewardAmount) ? rewardAmount : '0',
        tokenBifrostBfc
      ),
    [formatCurrencyValue, rewardAmount, tokenBifrostBfc]
  );
  const isDisabled = useMemo(() => !isNormalPositive(amount, true), [amount]);

  // handler
  const handleEverdexRemoveLiquidityClick = useCallback(() => {
    eventEverdexRemoveLiquidityClick(isTestnet);
    sendSentryEventEverdexRemoveLiquidityClick(isTestnet);
    setDialogAction({ isShow: true, type: 'everdex:removeLiquidity' });
  }, [setDialogAction, isTestnet]);
  const handleEverdexLpExitClick = useCallback(() => {
    eventEverdexUnstakingClick(isTestnet);
    sendSentryEventEverdexUnstakingClick(isTestnet);
    setDialogAction({
      isShow: true,
      type: 'everdex:lpExit',
      amount: formatDecimal(
        rewardAmount ?? '0',
        tokenBifrostBfc?.decimals ?? 18
      ),
    });
  }, [isTestnet, setDialogAction, rewardAmount, tokenBifrostBfc?.decimals]);

  return (
    <EarnDetailPanel
      header={
        <FlexBox $flex $justify="space-between" $align="center">
          <ImageLogoEverdex />

          <Tooltip placement="top" offset={16}>
            <TooltipTrigger>
              <HeaderTag>
                + {getNumberWithCurrencyReplace(rewardAmountValue)}
              </HeaderTag>
            </TooltipTrigger>
            <TooltipContent>
              <TooltipArrow />
              <StyledText $fontSize="xs" $fontWeight="medium">
                {formatAmount(rewardAmount)} {tokenBifrostBfc?.symbol ?? 'BFC'}
              </StyledText>
            </TooltipContent>
          </Tooltip>
        </FlexBox>
      }
      minHeight={216}
    >
      <EarnDetailRow isLoading={isLoading}>
        <FlexBox $flex $align="center" $gap="xxs">
          <FlexBox $width={44}>
            <TokenPairIcon size={24} tokens={everdexLpRewardPool.tokens} />
          </FlexBox>
          <StyledText $fontSize="md" $fontWeight="bold">
            {getNumberWithCurrencyReplace(amountValue)}
          </StyledText>
          <BadgeContainer>
            <StyledText
              $fontSize="xs"
              $textColor="green"
              $fontWeight="bold"
              $whiteSpace="nowrap"
            >
              {t('everdexSupply')}
            </StyledText>
          </BadgeContainer>
        </FlexBox>
        <FlexBox $flex $gap="xxs">
          {isDisabled ? (
            <Tooltip placement="top" offset={16}>
              <TooltipTrigger asChild>
                <Button
                  $variant="primary"
                  $padding="xxs"
                  $rounded="xs"
                  disabled
                >
                  <StyledText
                    $fontSize="xs"
                    $fontWeight="medium"
                    $whiteSpace="nowrap"
                  >
                    {t('bifiWithdraw')}
                  </StyledText>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <TooltipArrow />
                <StyledText $fontSize="xs" $fontWeight="medium">
                  {t('dashboard:requireLpExitTooltip')}
                </StyledText>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              $variant="primary"
              $padding="xxs"
              $rounded="xs"
              onClick={handleEverdexRemoveLiquidityClick}
            >
              <StyledText
                $fontSize="xs"
                $fontWeight="medium"
                $whiteSpace="nowrap"
              >
                {t('bifiWithdraw')}
              </StyledText>
            </Button>
          )}
        </FlexBox>
      </EarnDetailRow>
      {isNormalPositive(stakeAmount, true) ? (
        <EarnDetailRow isLoading={isLoading}>
          <FlexBox $flex $align="center" $gap="xxs">
            <TokenIcon isHideChain size={24} symbol="LP" />
            <Tooltip placement="top" offset={16}>
              <TooltipTrigger>
                <StyledText $fontSize="md" $fontWeight="bold">
                  {formatAmount(stakeAmount)} LP
                </StyledText>
              </TooltipTrigger>
              <TooltipContent>
                <TooltipArrow />
                <StyledText $fontSize="xs" $fontWeight="medium">
                  <div>
                    {t('everdexDeposit')}: {formatAmount(stakeAmount)} /{' '}
                    {formatAmount(lpAmount)} LP
                  </div>
                  <div>
                    {t('everdexLpStakeReward')}: {formatAmount(rewardAmount)}{' '}
                    {tokenBifrostBfc?.symbol ?? 'BFC'}
                  </div>
                </StyledText>
              </TooltipContent>
            </Tooltip>
            <BadgeContainer>
              <StyledText
                $fontSize="xs"
                $textColor="green"
                $fontWeight="bold"
                $whiteSpace="nowrap"
              >
                {t('everdexLPStaking')}
              </StyledText>
            </BadgeContainer>
          </FlexBox>
          <FlexBox $flex $gap="xxs">
            <FlexBox $flex $align="center" $gap="xxs">
              <Button
                $variant="primary"
                $padding="xxs"
                $rounded="xs"
                disabled={!isNormalPositive(stakeAmount, true)}
                onClick={handleEverdexLpExitClick}
              >
                <StyledText
                  $fontSize="xs"
                  $fontWeight="medium"
                  $whiteSpace="nowrap"
                >
                  {t('everdexUnstaking')}
                </StyledText>
              </Button>
            </FlexBox>
          </FlexBox>
        </EarnDetailRow>
      ) : (
        ''
      )}
    </EarnDetailPanel>
  );
}
